/**
 * The vocabulary and the index arithmetic behind a generated display name.
 *
 * **This file imports nothing.** `display-name.ts` pulls `@/i18n`, so a plain
 * `node --test` cannot load it; the arithmetic lives here so it can be tested
 * for real — the defect it replaces was invisible in every individual name.
 *
 * ## Why this exact hash
 *
 * The old multiplicative hash (`hash * 31 + c`) leaked parity: 31 is odd, the
 * `":noun"` salt's char sum is even, and the modulo (14) was even, so the noun
 * index always matched the adjective index's parity — measured at 100.0% over
 * 100k samples. Half the 196-name grid was unreachable while every single-word
 * frequency stayed a uniform 7.1%, which is why nothing looked wrong until
 * pairs were counted. FNV-1a + the MurmurHash3 `fmix32` finalizer fixes the
 * CLASS, not the instance: the avalanche makes every output bit depend on
 * every input bit, so an even modulo is safe. Changing the salt instead would
 * only move the collision — any even-sum salt reproduces it.
 *
 * ## Why one word + six Crockford Base32 characters
 *
 * Adjective+noun over 30x30 = 900 names collides almost immediately (38.66% at
 * 30 people, 95.81% at 75). 30 x 32^6 = 32.2 billion keeps a pair collision
 * under 1e-6 up to ~254 people in one list, degrading gradually past that —
 * and the Nearby list is unbounded (`MAX_NEARBY_RESULTS` bounds the PAGE
 * only). Six characters is a ceiling, not a choice: Base32 includes digits and
 * `validateDisplayName` rejects 7+ digits as a phone number, so a seventh
 * could make a suggested name unsaveable by its own user.
 *
 * The word is a NOUN because the translated adjective lists inflect feminine
 * (`Dorada`, `Sereine`) — alone they would assign every es/fr/pt user a
 * gender; nouns are neutral. English hid this because its adjectives do not
 * inflect.
 *
 * ## Duplicates are accepted, not repaired
 *
 * An earlier in-list disambiguator (scan loaded rows, suffix repeats) was
 * removed: it kept a parallel index of an unbounded list and went stale on all
 * six paths that REMOVE someone. A rare duplicate is a valid name — people are
 * told apart by avatar and profile, exactly as for two users who hand-pick the
 * same name (`display_name` has no unique constraint, nothing keys on it).
 */

/** Used when a locale has no list of its own. English, matching `en.json`. */
export const FALLBACK_NOUNS = [
  "Anchor",
  "Atlas",
  "Aurora",
  "Beacon",
  "Bloom",
  "Bridge",
  "Canyon",
  "Cedar",
  "Comet",
  "Delta",
  "Echo",
  "Ember",
  "Garden",
  "Grove",
  "Harbor",
  "Haven",
  "Heron",
  "Lantern",
  "Maple",
  "Meadow",
  "Muse",
  "Nova",
  "Orbit",
  "Pilot",
  "Ridge",
  "River",
  "Scout",
  "Summit",
  "Willow",
  "Zephyr",
];

export const MIN_DISPLAY_NAME_LENGTH = 3;
export const MAX_DISPLAY_NAME_LENGTH = 20;

/** FNV-1a's published 32-bit offset basis. */
export const FNV_OFFSET_BASIS = 2166136261;

/**
 * A stable index in `[0, modulo)` for a seed.
 *
 * FNV-1a from `basis`, then `fmix32`. `Math.imul` is used for the multiplies
 * because plain `*` on values above 2^53 loses the low bits that the finalizer
 * then spreads, which would quietly weaken exactly the property this provides.
 *
 * **`basis` is not decoration.** It is what stops several positions of one name
 * being derived from a single compressed state; see `POSITION_BASES`.
 */
export function seedToIndex(
  seed: string,
  modulo: number,
  basis: number = FNV_OFFSET_BASIS,
): number {
  let hash = basis;

  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  // fmix32 — the avalanche. Without this the low bits of the result are a
  // linear function of the input and an even `modulo` leaks them straight
  // through; see the header.
  hash ^= hash >>> 16;
  hash = Math.imul(hash, 2246822507);
  hash ^= hash >>> 13;
  hash = Math.imul(hash, 3266489909);
  hash ^= hash >>> 16;

  return (hash >>> 0) % modulo;
}

/**
 * One starting state per output position: six code characters and the word.
 *
 * Positions used to be salted by SUFFIX (`${seed}#0`), but FNV-1a consumes its
 * input left to right, so the seed was compressed to ONE 32-bit state before
 * any suffix — every position was a function of that state, and the whole name
 * carried at most 32 bits, not the 35 the 30 x 32^6 grid advertises. Measured:
 * 140 collisions per million real UUIDs where ~16 were expected. Pairwise
 * coverage across all 21 position pairs passed throughout — pairwise
 * independence does not imply joint independence, which is why the test now
 * checks positions against each other.
 *
 * Salting by BASIS makes each position hash the ENTIRE seed from its own
 * starting state. Measured afterwards: 1-8 collisions per 500k-UUID run,
 * against ~3.9 expected for a perfect space. The constants are published ones
 * (FNV, golden ratio, fmix32, xxHash primes) — their only property that
 * matters here is being distinct.
 */
const POSITION_BASES = [
  FNV_OFFSET_BASIS, // 0x811c9dc5
  0x01000193, // the FNV prime
  0x9e3779b9, // golden ratio
  0x85ebca6b, // fmix32 multiplier
  0xc2b2ae35, // fmix32 multiplier
  0x27d4eb2f, // xxHash prime
  0x165667b1, // xxHash prime
];

/**
 * Crockford Base32. `I`, `L`, `O` and `U` are absent by design — the first
 * three because they are misread as `1`, `1` and `0` when somebody reads a
 * name aloud or types it, and `U` so the encoding cannot produce accidental
 * profanity.
 */
export const DISCRIMINATOR_ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";

/** See the header: six is the ceiling the phone-number rule imposes. */
export const DISCRIMINATOR_LENGTH = 6;

/**
 * `"Noun A1B2C3"` for a seed. Deterministic — the same seed always produces
 * the same name, so onboarding can pre-fill a stable suggestion from the
 * user's id. The word and each code character come from separately-based
 * hashes of the WHOLE seed; POSITION_BASES explains why that independence is
 * load-bearing and how the test guards it.
 */
export function composeGeneratedName(nouns: readonly string[], seed: string): string {
  let discriminator = "";
  for (let position = 0; position < DISCRIMINATOR_LENGTH; position += 1) {
    discriminator += DISCRIMINATOR_ALPHABET.charAt(
      seedToIndex(seed, DISCRIMINATOR_ALPHABET.length, POSITION_BASES[position]),
    );
  }

  // The word takes the last basis. Every position hashes the whole seed; none
  // is derived from another position's state.
  const noun = nouns[seedToIndex(seed, nouns.length, POSITION_BASES[DISCRIMINATOR_LENGTH])];

  return `${noun} ${discriminator}`;
}
